import {
  AnalyticsClient,
  IAnalyticsDataPayload,
} from 'features/analytics/types/analytics';
import {
  AdobeLaunchClient,
  TealiumClient,
  GTMClient,
  Ga4Client,
  WayGtmClient,
} from 'features/analytics/clients';
import { get, post } from 'utils/axios';
import { type AxiosResponse } from 'axios';
import {
  ANALYTICS_EVENT,
  type IAnalyticsConfigEndpoint,
  AnalyticsConfigEndpoint,
  ANALYTICS_PROPERTY,
  IntegrationName,
  type IAnalyticsEventRequestSchema,
  AnalyticsEventEndpoint,
  Integrations,
  IntegrationGroup,
} from '@kouto/types';
import { format } from 'date-fns/fp/format';
import { debug } from 'utils/debug';
import { uuidv4 } from 'utils';

const STORAGE_TRACKING_ID = '__way_user_tracking_id';
const STORAGE_SENT_EVENTS = '__way_sent_analytics_events';
const STORAGE_SENT_EVENTS_MAX_AGE_HOURS = 24;
const STORAGE_SENT_EVENTS_MAX_COUNT = 50;

export type AnalyticsDataObserver = (
  appData: IAnalyticsDataPayload,
  newPayload: Partial<IAnalyticsDataPayload>,
) => void;

interface SentEvent {
  eventName: ANALYTICS_EVENT;
  payload: IAnalyticsDataPayload;
  timestamp: string;
  sessionId: string;
}

type AnalyticsProvider =
  (typeof Integrations)[IntegrationGroup.ANALYTICS][number];

export interface SendEventOptions {
  providers?: AnalyticsProvider[];
}

class AnalyticsManager {
  private providers: AnalyticsClient[] = [new WayGtmClient()];

  private observers: AnalyticsDataObserver[] = [];

  private appData: IAnalyticsDataPayload = {};

  private isReady = false;

  private providersInitialized = false;

  private hasOneTrustConsent = process.env.KOUTO_STAGE === 'development';

  private sessionId: string;

  private events: {
    eventName: ANALYTICS_EVENT;
    payload?: Record<string, string>;
    providers?: AnalyticsProvider[];
  }[] = [];

  private sentEvents: SentEvent[] = [];

  private static instance: AnalyticsManager;

  public static getInstance(): AnalyticsManager {
    if (!AnalyticsManager.instance) {
      AnalyticsManager.instance = new AnalyticsManager();
    }

    return AnalyticsManager.instance;
  }

  constructor() {
    this.sessionId = uuidv4();
    this.loadSentEvents();
  }

  // eslint-disable-next-line class-methods-use-this
  get userId(): string {
    const storedUser = localStorage.getItem(STORAGE_TRACKING_ID);
    if (storedUser) return storedUser;

    const userId = uuidv4();
    localStorage.setItem(STORAGE_TRACKING_ID, userId);
    return userId;
  }

  private loadSentEvents() {
    try {
      const storedEvents = localStorage.getItem(STORAGE_SENT_EVENTS);
      if (storedEvents) {
        this.sentEvents = JSON.parse(storedEvents);

        const maxAgeDate = new Date();
        maxAgeDate.setHours(
          maxAgeDate.getHours() - STORAGE_SENT_EVENTS_MAX_AGE_HOURS,
        );

        this.sentEvents = this.sentEvents.filter((event) => {
          const eventTime = new Date(event.timestamp);
          return eventTime > maxAgeDate;
        });

        this.saveSentEvents();
      }
    } catch (error) {
      debug('error', 'Failed to load sent events from storage', error);
      this.sentEvents = [];
    }
  }

  private saveSentEvents() {
    try {
      localStorage.setItem(
        STORAGE_SENT_EVENTS,
        JSON.stringify(this.sentEvents),
      );
    } catch (error) {
      debug('error', 'Failed to save sent events to storage', error);
    }
  }

  private isDuplicateRecentEvent(
    eventName: ANALYTICS_EVENT,
    payload: IAnalyticsDataPayload,
  ): boolean {
    const currentSessionId = this.sessionId;

    const recentEvent = this.sentEvents.at(-1);

    if (
      recentEvent?.eventName !== eventName ||
      recentEvent?.sessionId !== currentSessionId
    ) {
      return false;
    }

    const stringifyPayload = (payload: IAnalyticsDataPayload): string => {
      const normalized = { ...payload };
      delete normalized[ANALYTICS_PROPERTY.TimeStamp];
      delete normalized[ANALYTICS_PROPERTY.SessionId];
      delete normalized[ANALYTICS_PROPERTY.UserId];
      return JSON.stringify(normalized);
    };

    const recentNormalizedPayload = stringifyPayload(recentEvent?.payload);
    const currentNormalizedPayload = stringifyPayload(payload);

    return recentNormalizedPayload === currentNormalizedPayload;
  }

  private addToSentEvents(
    eventName: ANALYTICS_EVENT,
    payload: IAnalyticsDataPayload,
  ): void {
    const sentEvent: SentEvent = {
      eventName,
      payload: { ...payload },
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
    };

    this.sentEvents.push(sentEvent);

    // Keep only the last max count of events to prevent storage bloat
    if (this.sentEvents.length > STORAGE_SENT_EVENTS_MAX_COUNT) {
      this.sentEvents = this.sentEvents.slice(-STORAGE_SENT_EVENTS_MAX_COUNT);
    }

    this.saveSentEvents();
  }

  async setProviders(brandId: string) {
    // If this.isReady is true, we don't need to set the providers again
    if (this.isReady) return;

    if (!this.hasOneTrustConsent) return;

    this.appData[ANALYTICS_PROPERTY.BrandId] = brandId;
    const res = await get<AxiosResponse<IAnalyticsConfigEndpoint['response']>>(
      AnalyticsConfigEndpoint.url({
        brandId,
      }),
    ).catch(() => null);

    if (!res) {
      this.isReady = true;
      return;
    }

    const integrations = res.data;

    integrations.forEach((integration) => {
      switch (integration.type) {
        case IntegrationName.ADOBE_ANALYTICS:
          this.providers.push(new AdobeLaunchClient(integration));
          break;
        case IntegrationName.TEALIUM_ANALYTICS:
          this.providers.push(new TealiumClient(integration));
          break;
        case IntegrationName.GTM_ANALYTICS:
          this.providers.push(new GTMClient(integration));
          break;
        case IntegrationName.GA4_ANALYTICS:
          this.providers.push(new Ga4Client(integration));
          break;
        default:
          break;
      }
    });

    this.isReady = true;
  }

  setOneTrustConsent(hasOneTrustConsent: boolean) {
    this.hasOneTrustConsent = hasOneTrustConsent;
  }

  setAppData(payload: IAnalyticsDataPayload) {
    if (!this.hasOneTrustConsent) {
      return;
    }

    Object.assign(this.appData, payload);

    this.observers.forEach((observer) => {
      observer(this.getAppData(), payload);
    });
  }

  getAppData() {
    return Object.freeze({
      ...this.appData,
      [ANALYTICS_PROPERTY.SessionId]: this.sessionId,
      [ANALYTICS_PROPERTY.UserId]: this.userId,
    });
  }

  removeAppData(property: ANALYTICS_PROPERTY) {
    delete this.appData[property];
  }

  initProviders() {
    if (!this.hasOneTrustConsent) {
      return;
    }

    if (this.providersInitialized) {
      return;
    }
    this.providersInitialized = true;

    this.providers.forEach((provider) => {
      provider.init();
      debug('log', `${provider.constructor.name} provider initialized`);
    });

    this.events.forEach((event) => {
      this.triggerEvent(event.eventName, event.payload, event.providers);
    });
    this.events = [];
  }

  private resolveProviders(requested?: AnalyticsProvider[]) {
    const active = this.providers.reduce<AnalyticsProvider[]>((acc, p) => {
      if (p.provider) {
        acc.push(p.provider);
      }
      return acc;
    }, []);
    if (!requested) {
      return active;
    }
    return active.filter((p) => requested.includes(p));
  }

  private sendToWayApi(
    data: IAnalyticsEventRequestSchema['request'],
    requestedProviders?: AnalyticsProvider[],
  ) {
    post<
      IAnalyticsEventRequestSchema['response'],
      IAnalyticsEventRequestSchema['request']
    >(AnalyticsEventEndpoint.url({ brandId: this.appData.brandId || '' }), {
      ...data,
      providers: this.resolveProviders(requestedProviders),
    }).catch(() => null);
  }

  private getPayloadData(customPayload?: Record<string, string>) {
    return {
      timestamp: format('MM-dd-yyyy:HH:mm:SS', new Date()),
      ...this.getAppData(),
      ...customPayload,
    };
  }

  private triggerEvent(
    eventName: ANALYTICS_EVENT,
    customPayload?: IAnalyticsDataPayload,
    requestedProviders?: AnalyticsProvider[],
  ) {
    const payload = this.getPayloadData(customPayload);
    this.providers.forEach((p) => {
      if (requestedProviders && !requestedProviders.includes(p.provider)) {
        return;
      }
      p.sendEvent(eventName, payload);
    });
  }

  sendEvent(
    eventName: ANALYTICS_EVENT,
    customPayload?: IAnalyticsDataPayload,
    options?: SendEventOptions,
  ) {
    if (!this.hasOneTrustConsent) {
      return;
    }

    debug('log', 'Analytics Manager Event', {
      eventName,
      customPayload,
      payload: this.getAppData(),
      providers: options?.providers,
    });

    const payload = this.getPayloadData(customPayload);

    if (this.isDuplicateRecentEvent(eventName, payload)) {
      debug('log', 'Duplicate event detected, skipping send', {
        eventName,
        payload,
      });
      return;
    }

    this.addToSentEvents(eventName, payload);

    this.sendToWayApi(
      {
        event: eventName,
        payload,
      },
      options?.providers,
    );

    if (!this.isReady) {
      this.events.push({
        eventName,
        payload,
        providers: options?.providers,
      });

      return;
    }

    if (!this.providers.length) {
      return;
    }
    this.triggerEvent(eventName, customPayload, options?.providers);
  }

  addDataObserver(observer: AnalyticsDataObserver) {
    if (!this.hasOneTrustConsent) {
      return () => {};
    }

    this.observers.push(observer);

    return () => {
      this.observers = this.observers.filter((o) => o !== observer);
    };
  }
}

export const analyticsManager = AnalyticsManager.getInstance();
